<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ListingInquiriesAPI from 'dashboard/api/listingInquiries';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

// O account scope das chamadas é resolvido pelo ApiClient a partir da rota;
// mantemos o getter disponível para logs/tracking e guardas de UI futuras.
const accountId = useMapGetter('getCurrentAccountId');
const hasAccount = computed(() => Boolean(accountId.value));

const inquiries = ref([]);
const loading = ref(false);
const hasError = ref(false);
// Estado por inquiry: { [id]: { text: string, submitting: boolean } }
const replyState = reactive({});

const inquiriesCount = computed(() => inquiries.value.length);
const isEmpty = computed(
  () => !loading.value && !hasError.value && inquiriesCount.value === 0
);
const countLabel = computed(() =>
  inquiriesCount.value === 1
    ? '1 mensagem'
    : `${inquiriesCount.value} mensagens`
);

const ensureReplyState = id => {
  if (!replyState[id]) {
    replyState[id] = { text: '', submitting: false };
  }
  return replyState[id];
};

const inquiryClientName = inquiry => inquiry.client?.name || 'Cliente';
const inquiryClientId = inquiry =>
  inquiry.client?.id || inquiry.contactId || inquiry.contact_id;
const hasClientContext = inquiry =>
  hasAccount.value && Boolean(inquiryClientId(inquiry));
const clientHistoryRoute = inquiry => ({
  name: 'lais_lead_historico',
  params: { accountId: accountId.value, id: inquiryClientId(inquiry) },
  query: {
    tab: 'historico',
    source: 'mensagens-clientes',
    inquiry_id: inquiry.id,
  },
});

// Formatação relativa simples em pt-BR a partir de createdAt (ISO/string/ms).
const RELATIVE_UNITS = [
  { limit: 60, divisor: 1, singular: 'segundo', plural: 'segundos' },
  { limit: 3600, divisor: 60, singular: 'minuto', plural: 'minutos' },
  { limit: 86400, divisor: 3600, singular: 'hora', plural: 'horas' },
  { limit: 2592000, divisor: 86400, singular: 'dia', plural: 'dias' },
  { limit: 31536000, divisor: 2592000, singular: 'mês', plural: 'meses' },
  { limit: Infinity, divisor: 31536000, singular: 'ano', plural: 'anos' },
];

const relativeTime = createdAt => {
  if (!createdAt) return '';
  const timestamp = new Date(createdAt).getTime();
  if (Number.isNaN(timestamp)) return '';

  const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (diffSeconds < 5) return 'agora';

  const unit = RELATIVE_UNITS.find(u => diffSeconds < u.limit);
  const value = Math.floor(diffSeconds / unit.divisor);
  const label = value === 1 ? unit.singular : unit.plural;
  return `há ${value} ${label}`;
};

const sortedReplies = inquiry =>
  [...(inquiry.replies || [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

const fetchInquiries = async () => {
  if (!hasAccount.value) return;
  loading.value = true;
  hasError.value = false;
  try {
    const { data } = await ListingInquiriesAPI.list();
    inquiries.value = data?.inquiries || [];
    inquiries.value.forEach(inquiry => ensureReplyState(inquiry.id));
  } catch (error) {
    hasError.value = true;
    useAlert('Não foi possível carregar as mensagens dos clientes.');
  } finally {
    loading.value = false;
  }
};

const submitReply = async inquiry => {
  const state = ensureReplyState(inquiry.id);
  const text = state.text.trim();
  if (!text || state.submitting) return;

  state.submitting = true;
  try {
    const { data } = await ListingInquiriesAPI.reply(inquiry.id, text);
    const index = inquiries.value.findIndex(item => item.id === inquiry.id);
    if (index !== -1 && data) {
      inquiries.value.splice(index, 1, data);
    }
    state.text = '';
    useAlert('Resposta enviada com sucesso.');
  } catch (error) {
    useAlert('Não foi possível enviar a resposta. Tente novamente.');
  } finally {
    state.submitting = false;
  }
};

onMounted(fetchInquiries);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="flex h-full min-h-0 flex-col bg-lais-bg-app">
    <LaisHeader
      title="Mensagens dos clientes"
      helper="Veja e responda as perguntas e comentários privados que os clientes deixaram nos seus imóveis."
      :title-badge="loading ? '' : countLabel"
      sticky
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          size="sm"
          icon="i-lucide-refresh-ccw"
          :is-loading="loading"
          :disabled="loading"
          @click="fetchInquiries"
        >
          Atualizar
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-10">
      <div class="mx-auto flex w-full max-w-3xl flex-col gap-4">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex flex-col items-center justify-center gap-3 py-20 text-lais-text-muted"
        >
          <span
            class="i-lucide-loader-circle size-6 animate-spin"
            aria-hidden="true"
          />
          <p class="text-[14px]">Carregando mensagens...</p>
        </div>

        <!-- Erro -->
        <LaisCard v-else-if="hasError" density="cozy">
          <div class="flex flex-col items-center gap-3 py-8 text-center">
            <span
              class="i-lucide-triangle-alert size-8 text-lais-danger"
              aria-hidden="true"
            />
            <p class="text-[14px] text-lais-text-body">
              Não foi possível carregar as mensagens dos clientes.
            </p>
            <LaisPrimaryButton
              variant="ghost"
              size="sm"
              @click="fetchInquiries"
            >
              Tentar novamente
            </LaisPrimaryButton>
          </div>
        </LaisCard>

        <!-- Empty -->
        <LaisCard v-else-if="isEmpty" density="cozy">
          <div class="flex flex-col items-center gap-3 py-12 text-center">
            <span
              class="i-lucide-messages-square size-9 text-lais-text-muted"
              aria-hidden="true"
            />
            <p class="text-[15px] font-semibold text-lais-text">
              Nenhuma mensagem de cliente ainda.
            </p>
            <p class="max-w-sm text-[13px] text-lais-text-muted">
              Quando um cliente deixar uma pergunta ou comentário em um dos seus
              imóveis, ela aparecerá aqui para você responder.
            </p>
          </div>
        </LaisCard>

        <!-- Lista de inquiries -->
        <template v-else>
          <LaisCard
            v-for="inquiry in inquiries"
            :key="inquiry.id"
            density="cozy"
          >
            <article class="flex flex-col gap-4">
              <!-- Imóvel -->
              <div class="flex items-center gap-2">
                <span
                  class="i-lucide-building size-4 shrink-0 text-lais-primary"
                  aria-hidden="true"
                />
                <h2
                  class="truncate text-[14px] font-semibold text-lais-text font-redHatDisplay"
                  :title="inquiry.listingTitle"
                >
                  {{ inquiry.listingTitle }}
                </h2>
              </div>

              <!-- Pergunta do cliente -->
              <div class="flex items-start gap-3">
                <Avatar
                  v-if="!hasClientContext(inquiry)"
                  :src="inquiry.client?.avatar"
                  :name="inquiryClientName(inquiry)"
                  :size="36"
                  rounded-full
                />
                <RouterLink
                  v-else
                  :to="clientHistoryRoute(inquiry)"
                  class="shrink-0 rounded-full focus:outline-none focus-visible:shadow-lais-focus"
                  :aria-label="`Abrir histórico de ${inquiryClientName(inquiry)}`"
                >
                  <Avatar
                    :src="inquiry.client?.avatar"
                    :name="inquiryClientName(inquiry)"
                    :size="36"
                    rounded-full
                  />
                </RouterLink>
                <div class="flex min-w-0 flex-1 flex-col gap-1">
                  <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <RouterLink
                      v-if="hasClientContext(inquiry)"
                      :to="clientHistoryRoute(inquiry)"
                      class="text-[13px] font-semibold text-lais-text transition-colors hover:text-lais-primary focus:outline-none focus-visible:shadow-lais-focus"
                    >
                      {{ inquiryClientName(inquiry) }}
                    </RouterLink>
                    <span
                      v-else
                      class="text-[13px] font-semibold text-lais-text"
                    >
                      {{ inquiryClientName(inquiry) }}
                    </span>
                    <span class="text-[12px] text-lais-text-muted">
                      {{ relativeTime(inquiry.createdAt) }}
                    </span>
                    <RouterLink
                      v-if="hasClientContext(inquiry)"
                      :to="clientHistoryRoute(inquiry)"
                      class="inline-flex items-center gap-1 text-[12px] font-semibold text-lais-primary transition-colors hover:text-[#3d2966] focus:outline-none focus-visible:shadow-lais-focus"
                    >
                      <span
                        class="i-lucide-history size-3.5"
                        aria-hidden="true"
                      />
                      Ver histórico
                    </RouterLink>
                  </div>
                  <p
                    class="whitespace-pre-line break-words text-[14px] leading-relaxed text-lais-text-body"
                  >
                    {{ inquiry.text }}
                  </p>
                </div>
              </div>

              <!-- Thread de respostas -->
              <ul
                v-if="sortedReplies(inquiry).length"
                class="flex flex-col gap-2 border-t border-lais-border-soft pt-3"
              >
                <li
                  v-for="reply in sortedReplies(inquiry)"
                  :key="reply.id"
                  class="flex"
                  :class="reply.isOwnerReply ? 'justify-end' : 'justify-start'"
                >
                  <div
                    class="flex max-w-[85%] flex-col gap-1 rounded-lais px-3 py-2"
                    :class="
                      reply.isOwnerReply
                        ? 'bg-lais-primary text-white'
                        : 'bg-lais-bg-subtle text-lais-text-body'
                    "
                  >
                    <span
                      class="text-[11px] font-semibold"
                      :class="
                        reply.isOwnerReply
                          ? 'text-white/80'
                          : 'text-lais-text-muted'
                      "
                    >
                      {{
                        reply.isOwnerReply
                          ? 'Você'
                          : reply.authorName || 'Cliente'
                      }}
                      · {{ relativeTime(reply.createdAt) }}
                    </span>
                    <p
                      class="whitespace-pre-line break-words text-[13px] leading-relaxed"
                    >
                      {{ reply.text }}
                    </p>
                  </div>
                </li>
              </ul>

              <!-- Caixa de resposta -->
              <form
                class="flex flex-col gap-2 border-t border-lais-border-soft pt-3"
                @submit.prevent="submitReply(inquiry)"
              >
                <label :for="`reply-${inquiry.id}`" class="sr-only">
                  Responder a {{ inquiryClientName(inquiry) }}
                </label>
                <textarea
                  :id="`reply-${inquiry.id}`"
                  v-model="ensureReplyState(inquiry.id).text"
                  rows="2"
                  placeholder="Escreva sua resposta..."
                  :disabled="ensureReplyState(inquiry.id).submitting"
                  class="w-full resize-y rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-50 disabled:opacity-60"
                />
                <div class="flex justify-end">
                  <LaisPrimaryButton
                    type="submit"
                    size="sm"
                    icon="i-lucide-send"
                    :is-loading="ensureReplyState(inquiry.id).submitting"
                    :disabled="!ensureReplyState(inquiry.id).text.trim()"
                  >
                    Responder
                  </LaisPrimaryButton>
                </div>
              </form>
            </article>
          </LaisCard>
        </template>
      </div>
    </div>
  </div>
</template>
